import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import GenericForm from '../../sec_generic_form/component';
import Text from '@text';
import { t } from '@utils/i18n';
import { numFormatToCurrency } from '@utils';

import '../styles/styles.scss';

const GiftcardAction = (props) => {
  const { product, setNewAmount=() => {} } = props;
  const { giftcardDetails = {} } = product;

  const [formErrors] = useState({});
  const [formData, setFormData] = useState({
    amount: giftcardDetails.min,
  });

    /**
     * Format the input value of the manual price input field on blur.
     */
    const handleManualAmount = (e) => {
      const { min, max } = giftcardDetails;
      let inputvalue = parseFloat(e.target.value);
      const hasValue = !!e.target.value || inputvalue > 0;
      if (!hasValue) {
        return setFormData({ ...formData, manualAmount: '', amount: min });
      }

      if (inputvalue && inputvalue < min) {
        inputvalue = min;
      } else if (inputvalue && inputvalue > max) {
        inputvalue = max;
      }
      inputvalue = numFormatToCurrency(inputvalue);
      const validAmount = typeof inputvalue === 'string' ? inputvalue.replace(',', '.') : inputvalue;
      setFormData({ ...formData, manualAmount: inputvalue, amount: validAmount });
    };

  // Restrict the input of the manual amount input field. Only allow valid numbers (max. 2 decimal points)
  const restrictManualAmount = (value) => {
    if (value === '.') {
      return '0.';
    } else if (value === '0') {
      return '';
    }
    let validValue = `${value}`;
    validValue = validValue.replace(/[^0-9.,]+/g, '');
    validValue = validValue.replace(',', '.');
    const chunks = validValue.split('.');

    return chunks.splice(0, 2).join('.');
  };

  // Generic function to handle any updates to the form.
  const handleFormUpdate = (key, value) => {
    const inputvalue = value;
    let validAmount = typeof inputvalue === 'string' ? inputvalue.replace(',', '.') : inputvalue;
    // If input is for amount, always reset the manual amount input.
    if (key === 'amount') {
      return setFormData({
        ...formData,
        amount: validAmount,
        manualAmount: '',
      });
    }

    /*
     * If input is for manualAmount, always update the value of the amount input,
     * because the amount input is the value that is sent to the API
     */
    if (key === 'manualAmount') {
      validAmount = restrictManualAmount(validAmount);
      return setFormData({
        ...formData,
        manualAmount: validAmount,
        amount: validAmount,
      });
    }
    setFormData({ ...formData, [key]: inputvalue });
  };

  /*
   * Show a calculated total and update whenever amount changes
   * NOTE: ONLY USE THE total VARIABLE FOR DISPLAY PURPOSES
   */
  const [total, setTotal] = useState('1');
  useEffect(() => {
    const cardPrice = formData?.amount || product?.price?.regular?.[0]?.amount || 0;
    const displayedTotal = typeof cardPrice === 'string' ? cardPrice.replace(',', '.') : cardPrice;
    const calculatedTotal = parseFloat(displayedTotal);
    setTotal(calculatedTotal);
    setNewAmount(calculatedTotal);
  }, [formData.amount]);

  // Helper function for dynamicOptions, to mutate each optionn in the correct format
  const arrayToOptions = (array) => {
    const options = [];
    array.forEach(value => options.push({
      label: numFormatToCurrency(value),
      value,
    }));
    return options;
  };

  /**
   * Generate dynamic preset options base on the min and max of the giftcard.
   */
  const dynamicOptions = () => {
    const { min, max } = giftcardDetails;
    const small = [15, 25, 50, 75];
    const medium = [25, 50, 75, 100];
    const large = [50, 100, 150, 200];

    let options = arrayToOptions([min, max]);
    [small, medium, large].forEach((array) => {
      const isSmallerThanSmallest = min < array[0];
      const isLargerThanLargest = max > array[array.length - 1];
      if (isSmallerThanSmallest && isLargerThanLargest) {
        options = arrayToOptions(array);
      }
    });

    return options;
  };

  const formConfig = [{
    type: 'fields',
    children: [
      {
        name: 'amount',
        label: t('blackhawk.form.amount.auto.title'),
        className: 'col-md-12 input-container',
        htmlAttributes: {
          value: formData.amount,
          type: 'options',
          options: dynamicOptions(),
        },
        stacked: true,
        clientSideErrorMsg: '',
        errorMessages: {},
      },
      {
        name: 'manualAmount',
        label: t('blackhawk.form.amount.manual.title', {
        replacements: {
            min: numFormatToCurrency(giftcardDetails.min),
            max: numFormatToCurrency(giftcardDetails.max),
        },
        }),
        className: 'col-md-12 input-container',
        htmlAttributes: {
        value: formData.manualAmount,
        autoComplete: 'off',
        placeholder: t('blackhawk.form.amount.manual.placeholder'),
        type: 'text',
        onBlur: handleManualAmount,
        },
        stacked: true,
        clientSideErrorMsg: '',
        errorMessages: {},
      },
    ]
  }];

  return (
    <div className="pdp-primaryActions b2c is-giftcard">
      {!giftcardDetails.fixed && (
        <div className="giftcard-form form-container">
          <form onSubmit={e => e.preventDefault()}>
          <GenericForm
            name="giftcard-form"
            formConfig={formConfig}
            formData={formData}
            updateFormDataState={handleFormUpdate}
            isChild={true}
            formErrors={formErrors}
            />
          </form>
          {!giftcardDetails.fixed && (
            <div className="giftcards-total">
              <Text replacements={{ amount: numFormatToCurrency(total) }}>blackhawk.price.total</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GiftcardAction.propTypes = {
  store: PropTypes.object,
  product: PropTypes.object,
  setNewAmount: PropTypes.func,
};

export default GiftcardAction;
